import {useMemo, useState, useEffect} from 'react'
import {computeSignLayout, type SignDef, type SignInput} from '../../../lib/sign-catalog'
import {lvsRef, round4} from '../../../lib/utils'
import {MonoLabel} from '../../ui/mono-label'
import {UnderlineInput} from '../../ui/underline-field'
import {WarningPill} from '../../ui/warning-pill'

export interface SizeStepProps {
    readonly def: SignDef
    readonly input: SignInput
    readonly onChange: (patch: Partial<SignInput>) => void
}

const SIZE_STEP_MM = 10
const HARD_MAX_W = 5000
const HARD_MAX_H = 3000

const snap = (n: number) => Math.round(n / SIZE_STEP_MM) * SIZE_STEP_MM
const clamp = (n: number, lo: number, hi: number) => Math.min(Math.max(n, lo), hi)

/**
 * Lets the operator inflate the outer W/H beyond the text-derived auto
 * value. The text-derived value (with §9.19 floor applied via the
 * SignDef's catalog defaults) is the lower bound; the upper bound is a
 * UX-only hard cap (LVS 77-3:2016 sets no numeric maximum for 521/522).
 */
export function SizeStep({def, input, onChange}: SizeStepProps) {
    // Auto layout = no override. This is the lower bound for user input.
    const auto = useMemo(
        () => computeSignLayout(def, input, {ignoreOuter: true}).layout,
        [def, input]
    )
    // Round the auto value up to the next 10 mm — never let user input
    // dip below it, even by a sub-step fraction.
    const ceil10 = (n: number) => Math.ceil(n / SIZE_STEP_MM) * SIZE_STEP_MM
    const minW = Math.max(ceil10(auto.width), def.minSize.w)
    const minH = Math.max(ceil10(auto.height), def.minSize.h)

    // If text growth has pushed the auto-derived floor above the stored
    // override, bump the override up so the displayed field never sits
    // below the "min" hint. Done as an effect (not during render) so it
    // doesn't fire repeatedly on uncontrolled re-renders.
    useEffect(() => {
        const wBumped = input.outerWidth < minW
        const hBumped = input.outerHeight < minH
        if (!wBumped && !hBumped) return
        const patch: Partial<SignInput> = {
            ...(wBumped && {outerWidth: minW}),
            ...(hBumped && {outerHeight: minH})
        }
        onChange(patch)
    }, [minW, minH, input.outerWidth, input.outerHeight, onChange])

    // Live preview with the current override applied.
    const live = useMemo(() => computeSignLayout(def, input).layout, [def, input])

    return (
        <section className='space-y-6 max-w-xl'>
            <div className='space-y-1.5'>
                <MonoLabel>Auto izmērs (no teksta)</MonoLabel>
                <p className='font-mono text-sm text-foreground/80 tabular-nums'>
                    {round4(auto.width)} × {round4(auto.height)} mm
                </p>
                <p className='font-mono text-[10px] tracking-[0.15em] uppercase text-foreground/40'>
                    Min. {def.minSize.w} × {def.minSize.h} mm
                    {def.lvsRefs.length > 0
                        && ` (${def.lvsRefs.map(r => lvsRef(r, 'clause')).join(', ')})`}
                </p>
            </div>

            <div className='grid grid-cols-2 gap-6'>
                <SizeField
                    label='Platums (W)'
                    value={input.outerWidth}
                    min={minW}
                    max={HARD_MAX_W}
                    onCommit={outerWidth => onChange({outerWidth})}
                />
                <SizeField
                    label='Augstums (H)'
                    value={input.outerHeight}
                    min={minH}
                    max={HARD_MAX_H}
                    onCommit={outerHeight => onChange({outerHeight})}
                />
            </div>

            <div className='space-y-1.5'>
                <MonoLabel>Faktiskais izmērs</MonoLabel>
                <p className='font-mono text-sm text-foreground tabular-nums'>
                    {round4(live.width)} × {round4(live.height)} mm
                </p>
                <p className='font-mono text-[10px] tracking-[0.15em] uppercase text-foreground/40 tabular-nums'>
                    D = {round4(live.diagonal)} mm ({(live.diagonal / 1000).toFixed(3)} m)
                </p>
            </div>

            {live.warnings.some(w => w.kind === 'diagonal-above-max-band') && (
                <WarningPill>
                    Diagonāle pārsniedz tabulas{' '}
                    {lvsRef(input.bgColor === 'white' ? '9.15' : '9.14', 'table')} augšējo robežu
                </WarningPill>
            )}

            <button
                type='button'
                onClick={() => onChange({outerWidth: def.minSize.w, outerHeight: def.minSize.h})}
                className='font-mono text-xs tracking-[0.2em] uppercase text-primary hover:underline'
            >
                Atjaunot uz minimumu
            </button>

            <p className='font-mono text-[10px] tracking-[0.15em] uppercase text-foreground/40'>
                LVS 77-3:2016 nenosaka maksimālo izmēru šai zīmei; ievades augšējā robeža (
                {HARD_MAX_W}×{HARD_MAX_H} mm) ir tikai UI ierobežojums.
            </p>
        </section>
    )
}

interface SizeFieldProps {
    readonly label: string
    readonly value: number
    readonly min: number
    readonly max: number
    readonly onCommit: (value: number) => void
}

function SizeField({label, value, min, max, onCommit}: SizeFieldProps) {
    // Local string so the user can clear the field while typing without us
    // immediately snapping it back. Commit happens on blur / Enter.
    const [draft, setDraft] = useState(String(value))

    // Re-sync when the upstream value changes (e.g. "Atjaunot uz minimumu").
    useEffect(() => {
        setDraft(String(value))
    }, [value])

    const commit = () => {
        const n = Number(draft)
        if (!Number.isFinite(n)) {
            setDraft(String(value))
            return
        }
        const next = clamp(snap(n), min, max)
        setDraft(String(next))
        if (next !== value) onCommit(next)
    }

    return (
        <div className='flex flex-col gap-1.5'>
            <MonoLabel>{label}</MonoLabel>
            <UnderlineInput
                type='number'
                inputMode='numeric'
                step={SIZE_STEP_MM}
                min={min}
                max={max}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        e.preventDefault()
                        commit()
                    }
                }}
            />
            <p className='font-mono text-[10px] tracking-[0.15em] uppercase text-foreground/40 tabular-nums'>
                {min}–{max} mm · solis {SIZE_STEP_MM} mm
            </p>
        </div>
    )
}
